<template>
  <v-navigation-drawer
    ref="defectListDrawer"
    v-model="drawerOpen"
    app
    clipped
    right
    :width="defectListDrawerWidth"
    class="global-nav-drawer defect-list-drawer"
  >
    <div
      id="defect-list-explorer"
      class="solvit-defect-explorer d-flex flex-column"
      style="width: 100%; height: 100%; min-height: 0"
      tabindex="0"
      @keydown="onKeydown"
    >
      <v-card
        flat
        color="transparent"
        class="d-flex align-center justify-space-between flex-shrink-0 solvit-rail-subheader px-2"
      >
        <span class="text-caption font-weight-medium">
          {{ $t('defects.moduleListTitle') }}
          <span
            v-if="isViewMode"
            style="color: var(--v-primary-base); font-size: 11px"
          > · View</span>
        </span>
        <div class="d-flex align-center">
          <v-btn
            icon
            class="defect-list-drawer-close"
            :aria-label="$t('common.close')"
            @click="drawerOpen = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card>

      <!-- 모듈 리스트 안내 (결함 유형 필터는 지도 상단 툴바) -->
      <div
        v-if="isViewMode"
        class="defect-list-hint px-2 py-1 flex-shrink-0 text-caption text--secondary"
      >
        {{ $t('defects.defectViewModeHint') }}
      </div>

      <!-- 컬럼 헤더 — 리스트 행(icon · info · trailing)과 동일 flex 그리드 -->
      <div
        class="defect-list-column-header d-flex align-center flex-shrink-0"
        role="row"
      >
        <div
          class="d-flex align-center justify-center flex-shrink-0 solvit-de-icon-col"
          aria-hidden="true"
        />
        <div
          class="flex-grow-1 solvit-de-info-cell text-caption font-weight-bold text--secondary"
          style="min-width: 0"
        >
          {{ isViewMode ? $t('defects.moduleListColHierarchy') : $t('defects.moduleListColLine1') }}
          <div
            v-if="!isViewMode"
            class="module-list-col-sub text-caption font-weight-regular"
          >
            {{ $t('defects.moduleListColLine2') }}
          </div>
        </div>
        <div
          class="solvit-de-trailing-col d-flex align-center flex-shrink-0 pr-2"
          role="columnheader"
        >
          <span
            class="solvit-de-trailing-defect-col text-caption font-weight-bold text--secondary text-left"
          >
            {{ $t('defects.moduleListColDefect') }}
          </span>
          <span
            class="solvit-de-trailing-edit-col text-caption font-weight-bold text--secondary text-center"
          >
            {{ $t('defects.moduleListColEdit') }}
          </span>
        </div>
      </div>

      <!-- siteDesign/결함 API 응답 전 — rail 공통 skeleton -->
      <RailListSkeleton
        v-if="loading"
        variant="text-row"
        :count="10"
        :row-height="virtualRowHeight"
      />

      <div
        v-else-if="flatRows.length"
        class="defect-list-scroll-region flex-grow-1 d-flex flex-column"
        style="min-height: 0"
      >
        <!-- 핀은 overlay — 가상 스크롤 높이를 빼앗지 않음 (스크롤 중 높이 변동 방지) -->
        <div class="defect-hierarchy-sticky-overlay">
        <template v-for="sticky in pinnedStickyRows">
          <div
            v-if="sticky.id === 'array'"
            :key="`pinned_array_${nodeId(sticky.row)}`"
            class="defect-hierarchy-sticky-header"
            :style="{ height: virtualRowHeight + 'px' }"
          >
            <div
              class="defect-panel-header defect-panel-header--pinned d-flex align-center"
              :style="{ height: virtualRowHeight + 'px' }"
              @click="togglePanel(sticky.row)"
            >
              <div class="d-flex align-center justify-center flex-shrink-0 solvit-de-icon-col">
                <v-icon small>
                  {{ isNodeExpanded(sticky.row) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                </v-icon>
              </div>
              <div class="text-overflow-ellipsis flex-grow-1 solvit-de-info-cell body-2 font-weight-bold" style="min-width: 0">
                <RailListEllipsisTooltip
                  :text="headerLabel(sticky.row)"
                  :item-key="nodeId(sticky.row)"
                  :epoch="labelTooltipEpoch"
                  key-prefix="de-array"
                  text-class="font-weight-bold"
                  :text-style="{ color: nodeColor(sticky.row.kind) }"
                />
              </div>
              <div class="solvit-de-trailing-col d-flex flex-shrink-0 pr-2" aria-hidden="true">
                <span class="solvit-de-trailing-defect-col" />
                <span class="solvit-de-trailing-edit-col" />
              </div>
            </div>
          </div>
          <DefectStringHeaderRow
            v-else-if="sticky.id === 'string'"
            :key="`pinned_string_${nodeId(sticky.row)}`"
            class="defect-hierarchy-sticky-header"
            :row="sticky.row"
            :label="headerLabel(sticky.row)"
            :row-key="nodeId(sticky.row)"
            :tooltip-epoch="labelTooltipEpoch"
            :height="virtualRowHeight"
            :expanded="isNodeExpanded(sticky.row)"
            pinned
            @click="togglePanel(sticky.row)"
          />
        </template>
        </div>

        <v-virtual-scroll
          ref="defectVirtualScroll"
          :items="flatRows"
          :item-height="virtualRowHeight"
          :bench="18"
          class="flex-grow-1 defect-virtual-scroll"
          style="min-height: 0"
          @scroll.native="onDefectListVirtualScroll"
        >
          <template #default="{ item: row }">
            <!-- Array 헤더 -->
            <div
              v-if="row.kind === 'array_header'"
              v-show="!shouldHideRowForPin(row)"
              class="defect-panel-header d-flex align-center"
              :style="{ height: virtualRowHeight + 'px' }"
              @click="togglePanel(row)"
            >
              <div class="d-flex align-center justify-center flex-shrink-0 solvit-de-icon-col">
                <v-icon small>
                  {{ isNodeExpanded(row) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                </v-icon>
              </div>
              <div class="text-overflow-ellipsis flex-grow-1 solvit-de-info-cell body-2 font-weight-bold" style="min-width: 0">
                <RailListEllipsisTooltip
                  :text="headerLabel(row)"
                  :item-key="nodeId(row)"
                  :epoch="labelTooltipEpoch"
                  key-prefix="de-array"
                  text-class="font-weight-bold"
                  :text-style="{ color: nodeColor(row.kind) }"
                />
              </div>
              <div class="solvit-de-trailing-col d-flex flex-shrink-0 pr-2" aria-hidden="true">
                <span class="solvit-de-trailing-defect-col" />
                <span class="solvit-de-trailing-edit-col" />
              </div>
            </div>

            <!-- String 헤더 -->
            <DefectStringHeaderRow
              v-else-if="row.kind === 'string_header'"
              v-show="!shouldHideRowForPin(row)"
              :row="row"
              :label="headerLabel(row)"
              :row-key="nodeId(row)"
              :tooltip-epoch="labelTooltipEpoch"
              :height="virtualRowHeight"
              :expanded="isNodeExpanded(row)"
              @click="togglePanel(row)"
            />

          <!-- Module (결함 없음 — 리프) -->
          <div
            v-else-if="row.kind === 'panel'"
            class="defect-panel-header defect-panel-header--module d-flex align-center"
            :class="{ 'defect-panel-header--map-focused': isModuleRowMapFocused(row) }"
            :style="{ height: virtualRowHeight + 'px' }"
            @click="onModuleRowClick(row, $event)"
          >
            <div class="solvit-de-string-indent" style="width: 32px; flex-shrink: 0" />
            <div
              class="d-flex align-center justify-center flex-shrink-0 solvit-de-icon-col"
              style="padding-left: 0 !important"
              aria-hidden="true"
            />
            <div class="text-overflow-ellipsis flex-grow-1 solvit-de-info-cell body-2" style="min-width: 0">
              <span
                v-if="row.orphanModule"
                class="font-weight-medium text-truncate d-block"
                :style="{ color: nodeColor(row.kind) }"
              >
                {{ headerLabel(row) }}
              </span>
              <RailListEllipsisTooltip
                v-else
                :text="headerLabel(row)"
                :item-key="nodeId(row)"
                :epoch="labelTooltipEpoch"
                key-prefix="de-panel"
                text-class="font-weight-medium"
                :text-style="{ color: nodeColor(row.kind) }"
              />
            </div>
            <DefectModuleRowTrailing
              class="solvit-de-trailing-col"
              :defects="moduleRowDefects(row)"
              :show-edit="true"
              :empty-label="$t('defects.moduleListNoDefects')"
              :edit-label="$t('defects.editDefectRow')"
              :icon-src-fn="defectTypeIconSrc"
              :label-fn="defectTypeLabel"
              :on-icon-error="onDefectIconError"
              @edit="openDefectEdit"
            />
          </div>

          <!-- Module 단일 결함 -->
          <div
            v-else-if="row.kind === 'panel_single'"
            class="defect-row defect-row--single d-flex align-center"
            :class="{
              selected: row.defect && selectedDefectId === row.defect.seq,
              'defect-row--map-focused': isModuleRowMapFocused(row)
            }"
            :style="{ height: virtualRowHeight + 'px' }"
            @click="onModuleRowClick(row, $event)"
          >
            <div class="solvit-de-string-indent" style="width: 32px; flex-shrink: 0" />
            <div
              class="d-flex align-center justify-center flex-shrink-0 solvit-de-icon-col"
              style="padding-left: 0 !important"
              aria-hidden="true"
            />
            <div class="text-overflow-ellipsis flex-grow-1 solvit-de-info-cell body-2" style="min-width: 0">
              <div class="d-flex align-center font-weight-medium text-truncate">
                <span
                  v-if="row.orphanModule"
                  class="font-weight-medium text-truncate"
                  :style="{ color: nodeColor(row.kind) }"
                >
                  {{ headerLabel(row) }}
                </span>
                <RailListEllipsisTooltip
                  v-else
                  :text="headerLabel(row)"
                  :item-key="nodeId(row)"
                  :epoch="labelTooltipEpoch"
                  key-prefix="de-panel-single"
                  text-class="font-weight-medium"
                  :text-style="{ color: nodeColor(row.kind) }"
                />
                <v-chip
                  v-if="row.defect.mlDefectConfirmed === 'Y'"
                  x-small
                  color="primary"
                  class="ml-1 flex-shrink-0"
                  style="height: 14px; font-size: 9px"
                >AI</v-chip>
              </div>
              <div
                v-if="row.orphanModule && row.defect"
                class="text-caption text--secondary text-truncate"
                style="font-size: 0.7rem"
              >
                {{ defectDetailLine(row.defect) }}
              </div>
            </div>
            <DefectModuleRowTrailing
              class="solvit-de-trailing-col"
              :defects="moduleRowDefects(row)"
              :show-edit="true"
              :empty-label="$t('defects.moduleListNoDefects')"
              :edit-label="$t('defects.editDefectRow')"
              :icon-src-fn="defectTypeIconSrc"
              :label-fn="defectTypeLabel"
              :on-icon-error="onDefectIconError"
              @edit="openDefectEdit"
            />
          </div>

          <!-- 결함 행 -->
          <div
            v-else
            class="defect-row defect-row--child d-flex align-center"
            :class="{ selected: selectedDefectId === row.defect.seq }"
            :style="{ height: virtualRowHeight + 'px' }"
            @click="focusDefectRow(row.defect)"
          >
            <div style="width: 48px; flex-shrink: 0" />
            <div class="d-flex align-center justify-center flex-shrink-0 solvit-de-icon-col" style="padding-left: 0 !important">
              <img
                class="defect-list-pin-icon"
                :src="defectTypeIconSrc(row.defect)"
                :alt="defectTypeLabel(row.defect)"
                width="26" height="26"
                @error="onDefectIconError"
              />
            </div>
            <div class="text-overflow-ellipsis flex-grow-1 solvit-de-info-cell body-2" style="min-width: 0">
              <div class="d-flex align-center font-weight-medium text-truncate">
                <RailListEllipsisTooltip
                  :text="defectTypeLabel(row.defect)"
                  :item-key="'defect-type-' + row.defect.seq"
                  :epoch="labelTooltipEpoch"
                  key-prefix="de-defect-type"
                  text-class="font-weight-medium"
                />
                <v-chip
                  v-if="row.defect.mlDefectConfirmed === 'Y'"
                  x-small color="primary" class="ml-1 flex-shrink-0" style="height: 14px; font-size: 9px"
                >AI</v-chip>
              </div>
              <div class="text-caption text--secondary text-truncate" style="font-size: 0.7rem">
                <RailListEllipsisTooltip
                  :text="defectDetailLine(row.defect)"
                  :item-key="'defect-detail-' + row.defect.seq"
                  :epoch="labelTooltipEpoch"
                  key-prefix="de-defect-detail"
                  text-class="text-caption text--secondary"
                  :ellipsis="{ type: 'length', frontLength: 8, backLength: 14 }"
                />
              </div>
            </div>
            <div v-if="!isViewMode" class="d-flex flex-shrink-0 align-center pr-1 defect-row-actions" style="gap: 2px">
              <v-btn icon small @click.stop="openDefectEdit(row.defect)">
                <v-icon small>mdi-pencil-outline</v-icon>
              </v-btn>
              <v-btn icon small @click.stop="deleteDefectRow(row.defect)">
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </div>
          </div>
        </template>
        </v-virtual-scroll>
      </div>

      <div
        v-else
        class="d-flex align-center justify-center flex-grow-1 text-caption text--secondary px-3"
      >
        <div class="text-center">
          <v-icon large color="grey lighten-1" class="mb-2">mdi-information</v-icon>
          <div>{{ $t('defects.noDefectsFound') }}</div>
        </div>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
  import { defectListDrawerResizeMixin } from '@/ui/mixins/defectListDrawerResizeMixin';
  import { drawerSetup } from '../setups/drawerSetup';

  export default {
    name: 'DefectListDrawer',
    components: {
      RailListSkeleton: () => import('@/components/common/RailListSkeleton.vue'),
      RailListEllipsisTooltip: () => import('@/components/common/RailListEllipsisTooltip.vue'),
      DefectStringHeaderRow: () => import('./DefectStringHeaderRow.vue'),
      DefectModuleRowTrailing: () => import('./DefectModuleRowTrailing.vue')
    },
    mixins: [defectListDrawerResizeMixin, drawerSetup],
    props: {
      value: { type: Boolean, default: false },
      defects: { type: Array, default: () => [] },
      loading: { type: Boolean, default: false }
    }
  };
</script>

<style scoped>
  .defect-list-hint {
    background-color: var(--navBgColor);
  }
</style>

<style>
  .defect-list-drawer.v-navigation-drawer--right.v-navigation-drawer--fixed {
    right: 8px !important;
  }

  .defect-row #mdi-crosshairs-gps,
  .defect-row-actions .v-icon {
    color: var(--v-text-base);
  }

  #defect-list-explorer .defect-row-actions .v-btn {
    margin: 0;
  }

  #defect-list-explorer .defect-list-column-header {
    width: 100%;
    box-sizing: border-box;
    background-color: var(--navBgColor);
    border-bottom: 1px solid var(--v-borderSubtle-base);
    min-height: 32px;
    padding-top: 2px;
    padding-bottom: 2px;
  }

  #defect-list-explorer .defect-list-column-header .solvit-de-info-cell,
  #defect-list-explorer .defect-list-column-header .solvit-de-trailing-defect-col,
  #defect-list-explorer .defect-list-column-header .solvit-de-trailing-edit-col {
    line-height: 1.2;
    font-size: 0.75rem !important;
    font-weight: 600 !important;
  }

  #defect-list-explorer .defect-panel-header .solvit-de-info-cell,
  #defect-list-explorer .defect-panel-header .solvit-de-info-cell .font-weight-bold,
  #defect-list-explorer .defect-panel-header .solvit-de-info-cell.body-2 {
    font-size: 0.8125rem !important;
    line-height: 1.25 !important;
  }

  /* 닫기: small(×)은 Fitts에 너무 작음 — 헤더 메뉴 밀도와 맞춤 */
  #defect-list-explorer .defect-list-drawer-close.v-btn {
    width: 36px !important;
    height: 36px !important;
  }
  #defect-list-explorer .defect-list-drawer-close .v-icon {
    font-size: 22px !important;
  }

  #defect-list-explorer .explorer-header-table {
    width: 100%;
  }

  #defect-list-explorer .explorer-header-table thead th {
    padding-top: 6px !important;
    padding-bottom: 6px !important;
    font-size: 0.75rem !important;
    font-weight: 600 !important;
  }

  #defect-list-explorer .solvit-de-icon-col {
    width: 40px;
    min-width: 40px;
    max-width: 40px;
    padding: 4px 4px 4px 8px !important;
    box-sizing: border-box;
  }

  #defect-list-explorer .solvit-de-trailing-col {
    width: 160px;
    min-width: 160px;
    max-width: 160px;
    padding: 4px 8px 4px 4px !important;
    box-sizing: border-box;
    white-space: nowrap;
    gap: 8px;
  }

  #defect-list-explorer .solvit-de-trailing-defect-col {
    flex: 1 1 0;
    min-width: 104px;
    max-width: none;
    overflow: visible;
    justify-content: flex-start;
    text-align: left;
  }

  #defect-list-explorer .solvit-de-trailing-edit-col {
    flex: 0 0 32px;
    width: 32px;
  }

  #defect-list-explorer .solvit-de-trailing-col.defect-module-row-trailing {
    align-items: center;
  }

  #defect-list-explorer .defect-module-row-trailing {
    flex-shrink: 0;
    min-width: 0;
    max-width: 100%;
    padding-right: 4px;
    box-sizing: border-box;
  }

  #defect-list-explorer .defect-module-row-trailing .defect-module-edit-btn .v-icon,
  #defect-list-explorer .defect-module-row-trailing .defect-module-edit-btn__icon {
    color: var(--v-text-base);
    font-size: 22px !important;
  }

  #defect-list-explorer .defect-module-row-trailing .defect-module-edit-btn {
    width: 32px !important;
    height: 32px !important;
  }

  #defect-list-explorer .module-list-col-sub {
    line-height: 1.2;
    margin-top: 1px;
  }

  #defect-list-explorer .defect-list-pin-icon {
    display: block;
    width: 26px;
    height: 26px;
    max-width: 26px;
    max-height: 26px;
    object-fit: contain;
  }

  #defect-list-explorer .solvit-de-info-cell {
    padding: 4px 8px 4px 4px;
    box-sizing: border-box;
  }

  #defect-list-explorer .defect-virtual-scroll .defect-row,
  #defect-list-explorer .defect-virtual-scroll .defect-panel-header,
  #defect-list-explorer .defect-hierarchy-sticky-header .defect-panel-header {
    box-sizing: border-box;
    border-bottom: 1px solid var(--v-borderSubtle-base);
    overflow: visible;
    cursor: pointer;
  }

  #defect-list-explorer .defect-virtual-scroll .defect-panel-header,
  #defect-list-explorer .defect-hierarchy-sticky-header .defect-panel-header {
    background-color: var(--v-surface-base);
  }

  #defect-list-explorer .defect-virtual-scroll .defect-panel-header:hover,
  #defect-list-explorer .defect-hierarchy-sticky-header .defect-panel-header:hover {
    background-color: var(--solvit-row-selected-on-surface);
  }

  #defect-list-explorer .defect-virtual-scroll .defect-panel-header--map-focused,
  #defect-list-explorer .defect-virtual-scroll .defect-row--map-focused {
    background-color: rgba(76, 175, 80, 0.14);
    outline: 2px solid #2e7d32;
    outline-offset: -2px;
  }

  #defect-list-explorer .defect-virtual-scroll .defect-panel-header--map-focused .font-weight-medium,
  #defect-list-explorer .defect-virtual-scroll .defect-row--map-focused .font-weight-medium {
    font-weight: 700 !important;
  }

  #defect-list-explorer .defect-list-scroll-region {
    position: relative;
  }

  /* 리스트 높이는 고정, 핀만 위에 겹침. 우측 16px는 글로벌 스크롤바 폭 */
  #defect-list-explorer .defect-hierarchy-sticky-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 16px;
    z-index: 3;
  }

  #defect-list-explorer .defect-hierarchy-sticky-header {
    overflow: hidden;
    background: var(--v-surface-base);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  }

  #defect-list-explorer .defect-hierarchy-sticky-header:first-child {
    border-radius: 4px 4px 0 0;
  }

  #defect-list-explorer .defect-hierarchy-sticky-header:last-child:not(:only-child) {
    box-shadow: none;
  }

  #defect-list-explorer .defect-panel-header--pinned {
    border-bottom: 1px solid var(--v-borderSubtle-base);
  }

  #defect-list-explorer .defect-panel-type-chips {
    flex-wrap: wrap;
    gap: 3px;
    line-height: 1;
  }

  #defect-list-explorer .defect-panel-type-chip {
    max-width: 100%;
    flex-shrink: 1;
  }

  #defect-list-explorer .defect-panel-type-more {
    flex-shrink: 0;
    color: var(--v-text-secondary-base);
  }

  #defect-list-explorer .defect-virtual-scroll .v-virtual-scroll__item {
    overflow: visible;
  }

  .v-application.theme--light #defect-list-explorer .defect-virtual-scroll .defect-row.selected,
  .v-application.theme--dark #defect-list-explorer .defect-virtual-scroll .defect-row.selected {
    background-color: var(--solvit-row-selected-on-surface);
  }

  #defect-list-explorer .defect-virtual-scroll .defect-row.selected {
    outline: 2px solid var(--v-primary-base);
    outline-offset: -2px;
  }

  #defect-list-explorer .defect-virtual-scroll .defect-row:hover:not(.selected) {
    background-color: var(--v-surface-base);
  }
</style>
